import z from "zod";

const payKeeperErrorTypeValues = [
  "FAILED_TO_GET_PAYKEEPER_EVENTS",
  "FAILED_TO_GET_FUNDING",
  "FAILED_TO_GET_DISBURSEMENTS",
  "NO_FUNDS_TO_REQUEST",
  "FAILED_TO_REQUEST_FUNDS",
  "PAYKEEPER_MILESTONE_TYPE_NOT_FOUND",
  "FAILED_TO_PARSE_CLAIM_MILESTONE_RESPONSE",
  "FAILED_TO_UPLOAD_FILES",
  "FAILED_TO_GET_UPLOAD_URL_FOR_FILE",
  "FAILED_TO_DOWNLOAD_FILE_FROM_S3",
  "FAILED_TO_UPLOAD_FILE_TO_PAYKEEPER",
  "FAILED_TO_GET_MILESTONE_FOR_PROJECT",
  "FAILED_TO_FIND_MILESTONE_FOR_PROJECT",
  "TOO_MANY_MILESTONE_PHOTOS_FAILED_UPLOAD",
  "MILESTONE_PHOTOS_PARTIALLY_FAILED_UPLOAD",
  "NO_COMPLETED_WORK_PHOTOS_TO_CLAIM",
  "HOMEOWNER_MISSING_LAST_NAME",
  "FAILED_TO_CREATE_ESCROW",
  "FAILED_TO_CANCEL_ESCROW",
  "FAILED_TO_FLAG_ESCROW",
  "FAILED_TO_GET_ESCROW_STATUS",
  "FAILED_TO_INIT_FILE_UPLOADS",
  "FAILED_TO_CLAIM_MILESTONE",
] as const;
const payKeeperErrorTypeSchema = z.enum(payKeeperErrorTypeValues);
const PayKeeperErrorType = payKeeperErrorTypeSchema.enum;
type PayKeeperErrorType = z.infer<typeof payKeeperErrorTypeSchema>;

export type PayKeeperErrors =
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_GET_PAYKEEPER_EVENTS; data: { projectId?: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_GET_FUNDING; data: { externalId: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_GET_DISBURSEMENTS; data: { externalId: string } }
  | { errorType: typeof PayKeeperErrorType.NO_FUNDS_TO_REQUEST; data: { externalId: string; amountToRequest: number } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_REQUEST_FUNDS; data: { externalId: string; error: string | null } }
  | {
      errorType: typeof PayKeeperErrorType.PAYKEEPER_MILESTONE_TYPE_NOT_FOUND;
      data: { fundingMilestone: string; fundingMilestoneSchedule: string; externalId: string };
    }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_PARSE_CLAIM_MILESTONE_RESPONSE; data: { externalId: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_UPLOAD_FILES; data: { files: { id: string; name: string }[] } }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_GET_UPLOAD_URL_FOR_FILE;
      data: { fileId: string; fileName: string };
    }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_DOWNLOAD_FILE_FROM_S3; data: { fileId: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_UPLOAD_FILE_TO_PAYKEEPER; data: { fileId: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_GET_MILESTONE_FOR_PROJECT; data: { externalId: string } }
  | { errorType: typeof PayKeeperErrorType.FAILED_TO_FIND_MILESTONE_FOR_PROJECT; data: { externalId: string } }
  | {
      errorType: typeof PayKeeperErrorType.TOO_MANY_MILESTONE_PHOTOS_FAILED_UPLOAD;
      data: {
        externalId: string;
        payKeeperMilestoneType: string;
        requestedCount: number;
        uploadedCount: number;
        droppedCount: number;
        missingCount: number;
      };
    }
  | {
      errorType: typeof PayKeeperErrorType.MILESTONE_PHOTOS_PARTIALLY_FAILED_UPLOAD;
      data: {
        externalId: string;
        payKeeperMilestoneType: string;
        requestedCount: number;
        uploadedCount: number;
        droppedCount: number;
        missingCount: number;
      };
    }
  | {
      errorType: typeof PayKeeperErrorType.NO_COMPLETED_WORK_PHOTOS_TO_CLAIM;
      data: { externalId: string; payKeeperMilestoneType: string; missingCount: number };
    }
  | { errorType: typeof PayKeeperErrorType.HOMEOWNER_MISSING_LAST_NAME; data: { bidId: number } }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_CREATE_ESCROW;
      data:
        | { reason: "already-exists"; idempotencyKey: string; remiProjectId: string }
        | { reason: "other"; idempotencyKey: string; status?: number; errors?: string[] };
    }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_CANCEL_ESCROW;
      data:
        | { reason: "cannot-cancel"; idempotencyKey: string; escrowId: string }
        | { reason: "other"; idempotencyKey: string; escrowId: string; status?: number; errors?: string[] };
    }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_FLAG_ESCROW;
      data:
        | { reason: "cannot-flag"; idempotencyKey: string; escrowId: string }
        | { reason: "other"; idempotencyKey: string; escrowId: string; status?: number; errors?: string[] };
    }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_GET_ESCROW_STATUS;
      data:
        | { reason: "not-found"; escrowId: string }
        | { reason: "other"; escrowId: string; status?: number; errors?: string[] };
    }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_INIT_FILE_UPLOADS;
      data:
        | { reason: "invalid-files"; idempotencyKey: string; fileCount: number }
        | { reason: "idempotency-conflict"; idempotencyKey: string }
        | { reason: "other"; idempotencyKey: string; status?: number; errors?: string[] };
    }
  | {
      errorType: typeof PayKeeperErrorType.FAILED_TO_CLAIM_MILESTONE;
      data:
        | {
            reason: "cannot-claim";
            idempotencyKey: string;
            escrowId: string;
            milestoneCode: string;
          }
        | {
            reason: "other";
            idempotencyKey: string;
            escrowId: string;
            milestoneCode: string;
            status?: number;
            errors?: string[];
          };
    };

export const payKeeperErrorDetails: Record<PayKeeperErrorType, { sentryDescription: string }> = {
  FAILED_TO_GET_PAYKEEPER_EVENTS: { sentryDescription: "Failed to get paykeeper events" },
  FAILED_TO_GET_FUNDING: { sentryDescription: "Failed to get funding" },
  FAILED_TO_GET_DISBURSEMENTS: { sentryDescription: "Failed to get disbursements" },
  NO_FUNDS_TO_REQUEST: { sentryDescription: "No funds to request" },
  FAILED_TO_REQUEST_FUNDS: { sentryDescription: "Failed to request funds" },
  PAYKEEPER_MILESTONE_TYPE_NOT_FOUND: { sentryDescription: "Paykeeper milestone type not found" },
  FAILED_TO_PARSE_CLAIM_MILESTONE_RESPONSE: { sentryDescription: "Failed to parse claim milestone response" },
  FAILED_TO_UPLOAD_FILES: { sentryDescription: "Failed to upload files" },
  FAILED_TO_GET_UPLOAD_URL_FOR_FILE: { sentryDescription: "Failed to get upload url for file" },
  FAILED_TO_DOWNLOAD_FILE_FROM_S3: { sentryDescription: "Failed to download file from s3" },
  FAILED_TO_UPLOAD_FILE_TO_PAYKEEPER: { sentryDescription: "Failed to upload file to paykeeper" },
  FAILED_TO_GET_MILESTONE_FOR_PROJECT: { sentryDescription: "Failed to get milestone for project" },
  FAILED_TO_FIND_MILESTONE_FOR_PROJECT: { sentryDescription: "Failed to find milestone for project" },
  TOO_MANY_MILESTONE_PHOTOS_FAILED_UPLOAD: { sentryDescription: "Too many milestone photos failed to upload" },
  MILESTONE_PHOTOS_PARTIALLY_FAILED_UPLOAD: { sentryDescription: "Some milestone photos failed to upload" },
  NO_COMPLETED_WORK_PHOTOS_TO_CLAIM: { sentryDescription: "No completed-work photos to claim milestone with" },
  HOMEOWNER_MISSING_LAST_NAME: { sentryDescription: "Homeowner missing last name" },
  FAILED_TO_CREATE_ESCROW: { sentryDescription: "Failed to create escrow via PayKeeper partner endpoint" },
  FAILED_TO_CANCEL_ESCROW: { sentryDescription: "Failed to cancel escrow via PayKeeper partner endpoint" },
  FAILED_TO_FLAG_ESCROW: { sentryDescription: "Failed to flag escrow via PayKeeper partner endpoint" },
  FAILED_TO_GET_ESCROW_STATUS: { sentryDescription: "Failed to get escrow status via PayKeeper partner endpoint" },
  FAILED_TO_INIT_FILE_UPLOADS: {
    sentryDescription: "Failed to initialize file uploads via PayKeeper partner endpoint",
  },
  FAILED_TO_CLAIM_MILESTONE: {
    sentryDescription: "Failed to claim milestone via PayKeeper partner endpoint",
  },
};
